import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, BookOpen } from "lucide-react";

const STORAGE_EMAIL_KEY = "folio.device_email";
const STORAGE_TYPE_KEY = "folio.device_type";

export default function SendToDeviceDialog({ open, onOpenChange, bookId, bookTitle }) {
  const [deviceType, setDeviceType] = useState("kindle");
  const [deviceEmail, setDeviceEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Pre-fill from device the user last used
    const savedEmail = localStorage.getItem(STORAGE_EMAIL_KEY) || "";
    const savedType = localStorage.getItem(STORAGE_TYPE_KEY) || "kindle";
    setDeviceEmail(savedEmail);
    setDeviceType(savedType);
  }, [open]);

  const send = async () => {
    if (!deviceEmail) {
      toast.error(`Enter your ${deviceType === "kindle" ? "Send-to-Kindle" : "Kobo"} email first`);
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/library/${bookId}/send-to-device`, {
        device_email: deviceEmail,
        device_type: deviceType,
      });
      localStorage.setItem(STORAGE_EMAIL_KEY, deviceEmail);
      localStorage.setItem(STORAGE_TYPE_KEY, deviceType);
      toast.success(data.message);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send right now.";
      toast.error(typeof detail === "string" ? detail : "Could not send right now.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="send-device-dialog" className="max-w-md bg-paper border border-edge rounded-none">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Send className="w-4 h-4 text-terracotta" />
            <span className="label-eyebrow text-sage">Send to your device</span>
          </div>
          <DialogTitle className="font-serif text-2xl leading-tight">
            <em className="not-italic text-terracotta">{bookTitle}</em>
          </DialogTitle>
          <DialogDescription className="text-muted_ink text-sm">
            We'll email the PDF to your device. Reading on Kindle? Whitelist our sender at Amazon first (details below).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-3">
          <div className="grid grid-cols-2 gap-2">
            {["kindle", "kobo"].map((t) => (
              <button
                key={t}
                data-testid={`device-${t}`}
                onClick={() => setDeviceType(t)}
                className={`border px-4 py-3 text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2 ${
                  deviceType === t
                    ? "border-terracotta bg-terracotta text-paper"
                    : "border-edge bg-white hover:bg-linen"
                }`}
              >
                <BookOpen className="w-3.5 h-3.5" /> {t}
              </button>
            ))}
          </div>

          <div>
            <label className="label-eyebrow block mb-1">
              Your {deviceType === "kindle" ? "@kindle.com" : "Kobo"} email
            </label>
            <input
              data-testid="device-email-input"
              type="email"
              value={deviceEmail}
              onChange={(e) => setDeviceEmail(e.target.value)}
              placeholder={deviceType === "kindle" ? "yourname@kindle.com" : "yourname@kobo.com"}
              className="w-full border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink"
            />
            {deviceType === "kindle" && (
              <p className="text-[11px] text-muted_ink mt-2 leading-relaxed">
                Add <span className="font-mono">onboarding@resend.dev</span> (or whichever Folio sender email you see in the delivery) to your Kindle's approved-senders list at <a target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-ink" href="https://www.amazon.com/hz/mycd/myx#/home/settings/payment">Amazon → Manage Your Content and Devices</a>.
              </p>
            )}
          </div>

          <div className="border-t border-edge pt-4 flex justify-end gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="text-xs uppercase tracking-widest px-4 py-2 text-muted_ink hover:text-ink"
            >
              Cancel
            </button>
            <button
              data-testid="device-send-btn"
              onClick={send}
              disabled={sending}
              className="btn-primary disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send it"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
